'use client';

import { useMemo } from 'react';
import type { AriaGridListProps, Key } from 'react-aria';
import {
  ListDropTargetDelegate,
  ListKeyboardDelegate,
  useId,
} from 'react-aria';
import type { ListState } from 'react-stately';

export function useCarouselList<T>(
  ref: React.RefObject<HTMLDivElement | null>,
  state: ListState<T>,
  props: AriaGridListProps<T>
) {
  const id = useId();
  const { selectionManager: manager } = state;

  const keyboardDelegate = useMemo(() => {
    return new ListKeyboardDelegate({
      collection: state.collection,
      ref,
      disabledKeys: state.disabledKeys,
      orientation: 'horizontal',
      layout: 'stack',
      direction: 'ltr',
    });
  }, [ref, state.collection, state.disabledKeys]);

  const dropTargetDelegate = useMemo(() => {
    return new ListDropTargetDelegate(state.collection, ref, {
      layout: 'stack',
      direction: 'ltr',
      orientation: 'horizontal',
    });
  }, [ref, state.collection]);

  return {
    dropTargetDelegate,
    keyboardDelegate,
    carouselListProps: {
      'aria-label': 'Carousel',
      'aria-rowcount': state.collection.size,
      'aria-colcount': 1,
      role: 'grid',
      id,
      'aria-multiselectable':
        state.selectionManager.selectionMode === 'multiple' ? true : undefined,
      onKeyDown: (e: React.KeyboardEvent) => {
        function navigateToKey(key: Key) {
          manager.setFocusedKey(key);
          props.onAction?.(key);

          if (
            e.shiftKey &&
            manager.selectionMode === 'multiple' &&
            manager.selectedKeys.size > 0
          ) {
            manager.extendSelection(key);
          }
        }

        if (e.key === 'ArrowRight') {
          // @ts-expect-error FIXME(strictNullChecks)
          const nextKey = keyboardDelegate.getKeyRightOf?.(manager.focusedKey);
          if (nextKey != null) {
            e.preventDefault();
            navigateToKey(nextKey);
          }
          return;
        }

        if (e.key === 'ArrowLeft') {
          // @ts-expect-error FIXME(strictNullChecks)
          const nextKey = keyboardDelegate.getKeyLeftOf?.(manager.focusedKey);
          if (nextKey != null) {
            e.preventDefault();
            navigateToKey(nextKey);
          }
        }
      },
    },
  };
}
